// SafetyIncidents.js
import { useState, useEffect } from "react";
import {
  getSafetyIncidents,
  updateSafetyIncident,
  exportSafetyIncidentsCsv,
} from "../../service/Mainservice";
import ExportCsvButton from "../ExportCsvButton";
import Loader from "../../Loader/Loader";
import Modal from "../Model/Model";
import "./SafetyIncidents.css";

// MUI Icons
import EditIcon from "@mui/icons-material/Edit";
import WarningIcon from "@mui/icons-material/Warning";

const SafetyIncidents = () => {
  const [loading, setLoading] = useState(true);
  const [incidents, setIncidents] = useState([]);
  const [editModal, setEditModal] = useState({ open: false, incident: null });
  const [formData, setFormData] = useState({
    status: "",
    severityLevel: "",
    investigationNotes: "",
    policeReportLink: "",
  });

  const fetchIncidents = async () => {
    setLoading(true);
    try {
      const res = await getSafetyIncidents();
      setIncidents(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIncidents();
  }, []);

  const openEdit = (incident) => {
    setEditModal({ open: true, incident });
    setFormData({
      status: incident.status || "",
      severityLevel: incident.severityLevel || "",
      investigationNotes: incident.investigationNotes || "",
      policeReportLink: incident.policeReportLink || "",
    });
  };

  const handleUpdate = async () => {
    try {
      await updateSafetyIncident({
        incidentId: editModal.incident.id,
        ...formData,
      });
      setEditModal({ open: false, incident: null });
      fetchIncidents();
    } catch (err) {
      alert(err.response?.data?.message || "Update failed.");
    }
  };

  const getSeverityColor = (level) => {
    const map = {
      low: "status-pending",
      medium: "status-in-progress",
      high: "status-failed",
    };
    return map[level] || "";
  };

  const getStatusBadge = (status) => {
    const map = {
      investigating: "status-in-progress",
      resolved: "status-completed",
      closed: "status-failed",
    };
    return map[status] || "";
  };

  return (
    <div className="safety-page">
      <ExportCsvButton exportRequest={exportSafetyIncidentsCsv} filename="safety-incidents.csv" />
      {loading ? (
        <Loader />
      ) : (
        <div className="table-wrapper">
          <table className="data_table">
            <thead>
              <tr>
                <th>ID</th>
                <th>User</th>
                <th>Type</th>
                <th>Description</th>
                <th>Severity</th>
                <th>Status</th>
                <th>Investigation Notes</th>
                <th>Police Report</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {incidents.length === 0 ? (
                <tr>
                  <td colSpan="9">No incidents.</td>
                </tr>
              ) : (
                incidents.map((inc) => (
                  <tr key={inc.id}>
                    <td>{inc.id}</td>
                    <td>{inc.userProfile?.fullName || inc.userId}</td>
                    <td>{inc.userType}</td>
                    <td>{inc.description}</td>
                    <td>
                      <span
                        className={`status-badge ${getSeverityColor(inc.severityLevel)}`}
                      >
                        {inc.severityLevel}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`status-badge ${getStatusBadge(inc.status)}`}
                      >
                        {inc.status}
                      </span>
                    </td>
                    <td>{inc.investigationNotes || "-"}</td>
                    <td>
                      {inc.policeReportLink ? (
                        <a
                          href={inc.policeReportLink}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View
                        </a>
                      ) : (
                        "None"
                      )}
                    </td>
                    <td>
                      <button
                        className="btn-outline btn-sm"
                        onClick={() => openEdit(inc)}
                      >
                        <EditIcon fontSize="small" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Modal */}
      <Modal
        isOpen={editModal.open}
        onClose={() => setEditModal({ open: false, incident: null })}
        title="Update Safety Incident"
      >
        <div className="form-group">
          <label>Status</label>
          <select
            value={formData.status}
            onChange={(e) =>
              setFormData({ ...formData, status: e.target.value })
            }
          >
            <option value="investigating">Investigating</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </div>
        <div className="form-group">
          <label>Severity Level</label>
          <select
            value={formData.severityLevel}
            onChange={(e) =>
              setFormData({ ...formData, severityLevel: e.target.value })
            }
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </div>
        <div className="form-group">
          <label>Investigation Notes</label>
          <textarea
            value={formData.investigationNotes}
            onChange={(e) =>
              setFormData({ ...formData, investigationNotes: e.target.value })
            }
            rows="3"
          />
        </div>
        <div className="form-group">
          <label>Police Report Link</label>
          <input
            type="text"
            value={formData.policeReportLink}
            onChange={(e) =>
              setFormData({ ...formData, policeReportLink: e.target.value })
            }
            placeholder="https://..."
          />
        </div>
        <div className="modal-actions">
          <button
            className="btn-secondary"
            onClick={() => setEditModal({ open: false, incident: null })}
          >
            Cancel
          </button>
          <button className="btn-primary" onClick={handleUpdate}>
            Update
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default SafetyIncidents;
